import cn from 'classnames'
import dynamic from 'next/dynamic'
import React, { memo, useCallback, useMemo } from 'react'
// import CartHeader from 'src/components/global/Cart/CartHeader'
// import CartHeaderTitle from 'src/components/global/Cart/CartHeaderTitle'
// import CartSubContent from 'src/components/global/Cart/CartSubContent'
// import CartSubTitle from 'src/components/global/Cart/CartSubTitle'
// import { SpanDataList } from 'src/components/global/ListingPage/SpanDataList'
// import { LongDescItem } from 'src/components/global/Overview/LongDescItem'
import { IEvent } from 'src/graphql/generated/hooks'
import { FeaturesType } from 'src/types'
import { checkNotEmpty } from 'src/utils/checkNotEmpty'

const CartHeader = dynamic(() => import('src/components/global/Cart/CartHeader').then((mod) => mod.default), {
  loading: () => <p className="loading-placeholder"></p>,
  ssr: false,
})
const CartHeaderTitle = dynamic(() => import('src/components/global/Cart/CartHeaderTitle').then((mod) => mod.default), {
  loading: () => <p className="loading-placeholder"></p>,
  ssr: false,
})
const CartSubContent = dynamic(() => import('src/components/global/Cart/CartSubContent').then((mod) => mod.default), {
  loading: () => <p className="loading-placeholder"></p>,
  ssr: false,
})
const CartSubTitle = dynamic(() => import('src/components/global/Cart/CartSubTitle').then((mod) => mod.default), {
  loading: () => <p className="loading-placeholder"></p>,
  ssr: false,
})
const SpanDataList = dynamic(
  () => import('src/components/global/ListingPage/SpanDataList').then((mod) => mod.SpanDataList),
  {
    loading: () => <p className="loading-placeholder"></p>,
    ssr: false,
  },
)
const LongDescItem = dynamic(
  () => import('src/components/global/Overview/LongDescItem').then((mod) => mod.LongDescItem),
  {
    loading: () => <p className="loading-placeholder"></p>,
    ssr: false,
  },
)

interface EventDetailContainerProps {
  className?: string
  event: IEvent
}

/**
 * Primary UI component for user interaction
 */
const EventDetailContainer: React.FC<EventDetailContainerProps> = memo(({ className, event }) => {
  /* const audiences: FeaturesType[] =
    event.attributes?.audiences?.data.map((audience) => {
      const item: FeaturesType = {
        name: audience?.attributes?.name || '',
      }
      return item
    }) || [] */

  const audiences = useMemo<FeaturesType[]>(
    () =>
      event?.audiences.map((audience) => ({
        name: audience?.name || '',
      })) || [],
    [event?.audiences],
  )

  const description = event?.description || ''
  // const location = [event.attributes?.city, event.attributes?.country].filter((str) => !!str).join(', ')
  const location = useMemo(
    () => [event?.city, event?.country].filter((str) => !!str).join(', '),
    [event?.city, event?.country],
  )

  const organizer = event?.organizer
  const format = event?.formatId?.name || ''
  const duration = event?.durationId?.name || ''
  const recurrence = event?.recurrenceId?.name || ''
  const notes = event?.notes || ''

  const openWebsite = useCallback(() => {
    if (event?.website) {
      window.open(event.website)
    }
  }, [event?.website])

  return (
    <div className={cn(className, 'flex flex-col justify-center')}>
      <div className="flex flex-col justify-center">
        <div className="md:pl-0 pl-[1.25rem] pr-[1.25rem] !max-w-[1100px] w-full flex md:flex-row flex-col py-2">
          <div className="flex flex-col md:w-[50rem] w-full mr-[2.3125rem]">
            <div className="w-full border-[1px] border-mist rounded-[5px] mt-[2rem]">
              <CartHeader className="mb-[2.0rem]">
                <CartHeaderTitle>Event Details</CartHeaderTitle>
              </CartHeader>

              {description && <LongDescItem longDescription={description} />}

              <div className="flex md:flex-row flex-col">
                {organizer && (
                  <div className="flex flex-col w-1/3 pl-[2.0rem] mb-[2.0rem] ">
                    <CartSubTitle>ORGANIZATION</CartSubTitle>
                    <CartSubContent type="list">{organizer}</CartSubContent>
                  </div>
                )}
                {location && (
                  <div className="flex flex-col w-1/3 pl-[2.0rem] mb-[2.0rem]">
                    <CartSubTitle>LOCATION</CartSubTitle>
                    <CartSubContent type="list">{location}</CartSubContent>
                  </div>
                )}
                {format && (
                  <div className="flex flex-col w-1/3 pl-[2.0rem] mb-[2.0rem] ">
                    <CartSubTitle>FORMAT</CartSubTitle>
                    <CartSubContent type="list">{format}</CartSubContent>
                  </div>
                )}
              </div>

              <div className="flex md:flex-row flex-col">
                {duration && (
                  <div className="flex flex-col w-1/3 pl-[2.0rem] my-[2.0rem]">
                    <CartSubTitle>DURATION</CartSubTitle>
                    <CartSubContent type="list">{duration}</CartSubContent>
                  </div>
                )}
                {recurrence && (
                  <div className="flex flex-col w-1/3 pl-[2.0rem] my-[2.0rem]">
                    <CartSubTitle>RECURRENCE</CartSubTitle>
                    <CartSubContent type="list">{recurrence}</CartSubContent>
                  </div>
                )}
              </div>

              {audiences.length > 0 && (
                <div className="flex md:flex-row flex-col">
                  <div className="flex flex-col pl-[2.0rem] my-[2.0rem]">
                    <CartSubTitle>AUDIENCE</CartSubTitle>
                    <SpanDataList features={audiences} type="cart" />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col h-full md:w-[15rem] w-full mb-auto">
            {checkNotEmpty(notes) && <NotesSection notes={notes} />}
            {/* {checkNotEmpty(event.attributes?.notes) && (
              <div className="w-full border-[1px] border-mist rounded-[5px] mt-[2rem]">
                <CartHeader>
                  <CartHeaderTitle>Notes</CartHeaderTitle>
                </CartHeader>

                <div className="my-[2.0rem]">
                  <LongDescItem longDescription={event.attributes?.notes || ''} />
                </div>
              </div>
            )} */}

            <div className="w-full border-[1px] border-mist rounded-[5px] mt-[2rem]">
              <CartHeader>
                <CartHeaderTitle>More Information</CartHeaderTitle>
              </CartHeader>
              <CartSubContent type="list" className="h-[3.5625rem] px-[1.875rem] mt-[2.0rem] overflow-hidden">
                Learn more or register for this event:
              </CartSubContent>
              <div className="flex px-[2.0rem] mb-[2.0rem] mr-auto">
                <button
                  className="text-white font-secondary text-[14px] font-bold text-center rounded-[22.5px] px-[25px] bg-navy h-[40px]"
                  onClick={openWebsite}
                >
                  Visit Website
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

const NotesSection: React.FC<{ notes: string }> = React.memo(({ notes }) => (
  <div className="w-full border border-mist rounded-[5px] mt-8">
    <CartHeader>
      <CartHeaderTitle>Notes</CartHeaderTitle>
    </CartHeader>
    <div className="my-8">
      <LongDescItem longDescription={notes} />
    </div>
  </div>
))

EventDetailContainer.displayName = 'EventDetailContainer'
NotesSection.displayName = 'NotesSection'

export default EventDetailContainer
